import { useContext } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttSelectMship,
    SttFormDate,
    SttFormTextarea,
    SttSelectPForm,
    SttFormText,
    SttFormCheck,
} from '../../all';
import { formatPriceByLocale } from '../../../lib/utils';
import DiscountList from '../../../components/discounts/DiscountsList';
import constants from '../../../config/constants';

type Discount = {
    id: number;
    name: string;
    discountPercentage: number;
    checked?: boolean;
};

type Enrollment = {
    price?: number;
    paymentForm?: string;
    priceShape?: {
        currency?: string;
        locale?: string;
    };
};

type User = {
    mship: {
        fullName?: string;
        [key: string]: unknown;
    };
};

type Member = {
    id?: number;
    name: string;
    ini?: string;
    end?: string;
    notes?: string;
    paymentFormFee?: string;
    user: User;
    enrollment?: Enrollment;
    group: {
        fee?: number;
        feeShape?: {
            currency?: string;
            locale?: string;
        };
    };
    expirationCheck?: boolean;
};

type Group = {
    id: number;
    type?: string;
    enrollmentPrice?: number;
    feePrice?: number;
    expiration?: string | null;
};

type Props = {
    member: Member;
    creating: boolean;
    group: Group;
    handleChange: (params: { name: string; value: unknown }) => void;
    discounts?: Discount[];
    handleChangeDiscounts?: (params: { name: number; value: unknown }) => void;
};

export default function SttGroupMemberDialogDetail({
    member,
    creating,
    group,
    handleChange,
    discounts,
    handleChangeDiscounts,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    return (
        <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                {creating ? (
                    <SttSelectMship
                        caption={t('generic.name')}
                        mship={member.user.mship as any}
                        onSelect={(mship: unknown) =>
                            handleChange({ name: 'mship', value: mship })
                        }
                    />
                ) : (
                    <SttFormText
                        disabled
                        caption={t('generic.name')}
                        defVal={member.name}
                    />
                )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <SttFormDate
                    caption={t('SignupFrom')}
                    name="ini"
                    defVal={member.ini}
                    onChange={handleChange as any}
                />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <SttFormDate
                    caption={t('Until')}
                    name="end"
                    defVal={member.end}
                    onChange={handleChange as any}
                />
            </Grid>

            <Grid size={{ xs: 12 }}>
                <SttFormTextarea
                    caption={t('Notes')}
                    name="notes"
                    defVal={member.notes}
                    onChange={handleChange as any}
                />
            </Grid>

            {group.type !== 'SPORT' && (
                <>
                    {group.enrollmentPrice! > 0 && (
                        <Grid size={{ xs: 12, md: 6 }}>
                            <SttSelectPForm
                                name="paymentFormSignup"
                                disabled={!creating}
                                caption={`${t('generic.inscription')} ${
                                    member.enrollment
                                        ? `[ ${formatPriceByLocale(
                                              member.enrollment.price,
                                              member?.enrollment?.priceShape
                                                  ?.currency,
                                              member?.enrollment?.priceShape
                                                  ?.locale,
                                          )} ]`
                                        : ''
                                }`}
                                defVal={member.enrollment?.paymentForm as any}
                                activePFs={[
                                    '',
                                    constants.payment.paymentForms.purse.name,
                                    constants.payment.paymentForms.cash.name,
                                    constants.payment.paymentForms.bank.name,
                                    cxt?.sc?.dataphoneConnected
                                        ? constants.payment.paymentForms
                                              .dataphoneConnected.name
                                        : constants.payment.paymentForms
                                              .dataphone.name,
                                    constants.payment.paymentForms.receipt.name,
                                    constants.payment.paymentForms.free.name,
                                ]}
                                onChange={handleChange as any}
                            />
                        </Grid>
                    )}
                    {group.feePrice! > 0 && (
                        <Grid size={{ xs: 12, md: 6 }}>
                            <SttSelectPForm
                                name="paymentFormFee"
                                caption={`${t('generic.paymentForm')}, (${t('generic.fee')}: ${formatPriceByLocale(
                                    member.group.fee,
                                    member?.group?.feeShape?.currency,
                                    member?.group?.feeShape?.locale,
                                )})`}
                                defVal={member.paymentFormFee as any}
                                activePFs={[
                                    '',
                                    cxt?.sc?.modules?.includes('receipt') &&
                                        constants.payment.paymentForms.receipt
                                            .name,
                                    cxt?.sc?.TPVFeePayment &&
                                        constants.payment.paymentForms.tpv.name,
                                ]}
                                onChange={handleChange as any}
                            />
                        </Grid>
                    )}

                    {creating &&
                        discounts &&
                        discounts.length > 0 &&
                        (group.feePrice || group.enrollmentPrice) && (
                            <Grid size={{ xs: 12 }}>
                                <Grid container>
                                    {discounts.map((discount: any) => (
                                        <Grid
                                            key={discount.id}
                                            size={{ xs: 12 }}
                                        >
                                            <SttFormCheck
                                                name={discount.id}
                                                caption={`${discount.name} - ${discount.discountPercentage}%`}
                                                checked={discount.checked}
                                                onChange={
                                                    handleChangeDiscounts as any
                                                }
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        )}

                    {!creating && discounts && discounts.length > 0 && (
                        <Grid size={{ xs: 12 }}>
                            <DiscountList
                                discounts={discounts}
                                showEmptyMessage={false}
                            />
                        </Grid>
                    )}

                    {creating &&
                        group.expiration &&
                        group.expiration !== '0000-00-00 00:00:00' && (
                            <Grid size={{ xs: 12, md: 6 }}>
                                <SttFormCheck
                                    name="expirationCheck"
                                    caption={t('generic.expiration')}
                                    checked={member?.expirationCheck}
                                    onChange={handleChange as any}
                                />
                            </Grid>
                        )}
                </>
            )}
        </Grid>
    );
}
